import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { UserInfo, LoginResponse, RefreshTokenResponse } from '../api/JsdhNotifikaceService/generated';

interface AuthState {
    user: UserInfo | null;
    accessToken: string | null;
    refreshToken: string | null;
    isLoading: boolean;
}

interface AuthActions {
    login: (response: LoginResponse) => void;
    logout: () => void;
    updateUser: (user: UserInfo) => void;
    updateTokens: (response: RefreshTokenResponse) => void;
    setLoading: (loading: boolean) => void;
}

interface AuthStore extends AuthState, AuthActions {
    // Computed getters
    isAuthenticated: boolean;
    isAdmin: boolean;
    // Legacy alias
    token: string | null;
}

// Legacy keys for migration
const LEGACY_ACCESS_TOKEN_KEY = 'auth_access_token';
const LEGACY_REFRESH_TOKEN_KEY = 'auth_refresh_token';
const LEGACY_USER_KEY = 'auth_user';
const LEGACY_TOKEN_KEY = 'auth_token';

// Migrate from old localStorage keys
const migrateFromLegacy = (): Partial<AuthState> => {
    try {
        let accessToken = localStorage.getItem(LEGACY_ACCESS_TOKEN_KEY);
        const refreshToken = localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
        const userStr = localStorage.getItem(LEGACY_USER_KEY);

        // Try legacy token key
        if (!accessToken) {
            accessToken = localStorage.getItem(LEGACY_TOKEN_KEY);
        }

        if (accessToken && userStr) {
            const user = JSON.parse(userStr) as UserInfo;

            // Clean up legacy keys after migration
            localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
            localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
            localStorage.removeItem(LEGACY_USER_KEY);
            localStorage.removeItem(LEGACY_TOKEN_KEY);

            return { accessToken, refreshToken, user };
        }
    } catch {
        // Failed to migrate legacy auth data
    }
    return {};
};

// Check if we have persisted auth data synchronously
const getInitialState = (): Partial<AuthState> => {
    try {
        const persistedData = localStorage.getItem('auth-storage');
        if (persistedData) {
            const parsed = JSON.parse(persistedData);
            if (parsed?.state?.accessToken && parsed?.state?.user) {
                return {
                    accessToken: parsed.state.accessToken,
                    refreshToken: parsed.state.refreshToken,
                    user: parsed.state.user,
                    isLoading: false,
                };
            }
        }
        // Check legacy keys
        const legacyToken = localStorage.getItem(LEGACY_ACCESS_TOKEN_KEY) || localStorage.getItem(LEGACY_TOKEN_KEY);
        const legacyUser = localStorage.getItem(LEGACY_USER_KEY);
        if (legacyToken && legacyToken !== 'undefined' && legacyUser) {
            const user = JSON.parse(legacyUser);
            return {
                accessToken: legacyToken,
                refreshToken: localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY),
                user,
                isLoading: false,
            };
        }
    } catch {
        // Error reading initial state
    }
    return { isLoading: false };
};

const initialState = getInitialState();

export const useAuthStore = create<AuthStore>()(
    persist(
        (set) => ({
            // State - use initial state from localStorage for synchronous hydration
            user: initialState.user ?? null,
            accessToken: initialState.accessToken ?? null,
            refreshToken: initialState.refreshToken ?? null,
            isLoading: initialState.isLoading ?? false,

            // Computed values based on initial state
            isAuthenticated: !!(initialState.accessToken && initialState.user),
            isAdmin: initialState.user?.role === 'ROLE_ADMIN' || initialState.user?.role === 'ADMIN'
                || initialState.user?.role === 'ROLE_SUPER_ADMIN' || initialState.user?.role === 'SUPER_ADMIN',
            token: initialState.accessToken ?? null,

            // Actions
            login: (response: LoginResponse) => {
                // Support both old format (token) and new format (accessToken)
                const accessToken = response.accessToken || response.token;
                const refreshToken = response.refreshToken || null;
                const user = response.user;
                set({
                    accessToken,
                    refreshToken,
                    user,
                    isLoading: false,
                    isAuthenticated: !!accessToken && !!user,
                    isAdmin: user?.role === 'ROLE_ADMIN' || user?.role === 'ADMIN'
                        || user?.role === 'ROLE_SUPER_ADMIN' || user?.role === 'SUPER_ADMIN',
                    token: accessToken,
                });
            },

            logout: () => {
                set({
                    accessToken: null,
                    refreshToken: null,
                    user: null,
                    isLoading: false,
                    isAuthenticated: false,
                    isAdmin: false,
                    token: null,
                });
            },

            updateUser: (user: UserInfo) => {
                set((state) => ({
                    user,
                    isAdmin: user?.role === 'ROLE_ADMIN' || user?.role === 'ADMIN'
                        || user?.role === 'ROLE_SUPER_ADMIN' || user?.role === 'SUPER_ADMIN',
                    isAuthenticated: !!state.accessToken && !!user,
                }));
            },

            updateTokens: (response: RefreshTokenResponse) => {
                const { accessToken, refreshToken } = response;
                set((state) => ({
                    accessToken,
                    refreshToken,
                    token: accessToken,
                    isAuthenticated: !!accessToken && !!state.user,
                }));
            },

            setLoading: (isLoading: boolean) => {
                set({ isLoading });
            },
        }),
        {
            name: 'auth-storage',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                user: state.user,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
            }),
            onRehydrateStorage: () => {
                return (state) => {
                    // Sync rehydration already happened in getInitialState
                    // State updates happen via the returned state object, not setState
                    if (state) {
                        state.isAuthenticated = !!state.accessToken && !!state.user;
                        state.isAdmin = state.user?.role === 'ROLE_ADMIN' || state.user?.role === 'ADMIN'
                            || state.user?.role === 'ROLE_SUPER_ADMIN' || state.user?.role === 'SUPER_ADMIN';
                        state.token = state.accessToken;
                        state.isLoading = false;
                    }
                };
            },
        }
    )
);

// Direct access for axios interceptor (outside React)
// With fallback to localStorage for cases when store is not yet rehydrated
export const getAuthState = () => useAuthStore.getState();

// Helper to check if token is valid (not null, undefined, or string "undefined")
const isValidToken = (token: string | null | undefined): token is string => {
    return !!token && token !== 'undefined' && token !== 'null';
};

export const getAccessToken = (): string | null => {
    const storeToken = useAuthStore.getState().accessToken;
    if (isValidToken(storeToken)) {
        return storeToken;
    }

    // Fallback: check localStorage directly (for pre-rehydration or legacy)
    try {
        const persistedData = localStorage.getItem('auth-storage');
        if (persistedData) {
            const parsed = JSON.parse(persistedData);
            if (isValidToken(parsed?.state?.accessToken)) {
                return parsed.state.accessToken;
            }
        }
        // Legacy fallback
        const legacyToken = localStorage.getItem(LEGACY_ACCESS_TOKEN_KEY) || localStorage.getItem(LEGACY_TOKEN_KEY);
        if (isValidToken(legacyToken)) {
            return legacyToken;
        }
        // Clean up invalid legacy tokens
        if (legacyToken === 'undefined' || legacyToken === 'null') {
            localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
            localStorage.removeItem(LEGACY_TOKEN_KEY);
        }
        return null;
    } catch {
        return null;
    }
};

export const getRefreshToken = (): string | null => {
    const storeToken = useAuthStore.getState().refreshToken;
    if (isValidToken(storeToken)) return storeToken;

    // Fallback: check localStorage directly
    try {
        const persistedData = localStorage.getItem('auth-storage');
        if (persistedData) {
            const parsed = JSON.parse(persistedData);
            if (isValidToken(parsed?.state?.refreshToken)) {
                return parsed.state.refreshToken;
            }
        }
        // Legacy fallback
        const legacyToken = localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
        if (isValidToken(legacyToken)) {
            return legacyToken;
        }
        // Clean up invalid legacy tokens
        if (legacyToken === 'undefined' || legacyToken === 'null') {
            localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
        }
        return null;
    } catch {
        return null;
    }
};
